'use client'

import { useEffect, useState } from 'react'
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from 'framer-motion'
import { Code2 } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen } from './demoHooks'

// App development: one app, three platforms from one codebase. The device
// frame changes around the same screen (iPhone, Android, web), and the
// balance counts and the chart draws each time, like the real thing would.

const PLATFORMS = [
  { key: 'ios', label: 'iPhone' },
  { key: 'android', label: 'Android' },
  { key: 'web', label: 'Web app' },
]
const CHART = 'M0,62 C18,58 30,46 48,48 C66,50 78,30 96,28 C114,26 126,36 144,22 C162,8 176,14 190,6'

function Balance({ value, run }) {
  const reduce = useReducedMotion()
  const mv = useMotionValue(reduce ? value : 0)
  const text = useTransform(mv, (v) => `$${Math.round(v).toLocaleString('en-US')}`)
  useEffect(() => {
    if (!run) return undefined
    if (reduce) { mv.set(value); return undefined }
    mv.set(0)
    const controls = animate(mv, value, { duration: 1.1, ease: [0.22, 1, 0.36, 1] })
    return () => controls.stop()
  }, [run, value, mv, reduce])
  return <motion.span className="svd-app-balance">{text}</motion.span>
}

function AppScreen({ platform, run }) {
  return (
    <div className="svd-app-screen">
      <div className="svd-app-top">
        <span>{platform === 'web' ? 'Dashboard' : 'Hi, Alex'}</span>
        <span className="svd-app-avatar" aria-hidden="true">A</span>
      </div>
      <small>Portfolio balance</small>
      <Balance value={124580} run={run} />
      <span className="svd-app-change">▲ 12.4% this month</span>
      <svg viewBox="0 0 190 70" className="svd-app-chart" aria-hidden="true">
        <motion.path d={CHART} fill="none" stroke="#1684ea" strokeWidth="3" strokeLinecap="round" initial={{ pathLength: 0 }} animate={run ? { pathLength: 1 } : {}} transition={{ duration: 1.2, ease: 'easeInOut' }} />
      </svg>
      <span className="svd-app-cta">View portfolio</span>
    </div>
  )
}

export default function DeviceSwitch() {
  const [ref, onScreen] = useOnScreen()
  const [platform, setPlatform] = useState('ios')
  const [auto, setAuto] = useState(true)

  useEffect(() => {
    if (!auto || !onScreen) return undefined
    const timer = setTimeout(() => {
      setPlatform((p) => PLATFORMS[(PLATFORMS.findIndex((x) => x.key === p) + 1) % PLATFORMS.length].key)
    }, 3800)
    return () => clearTimeout(timer)
  }, [auto, onScreen, platform])

  const pick = (key) => { setAuto(false); setPlatform(key) }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-devices"
      title="One app, every screen"
      label="Demo: the same app shown on iPhone, Android and the web from one codebase"
      tabs={PLATFORMS}
      activeTab={platform}
      onTab={pick}
    >
      <div className="svd-device-stage">
        <motion.div
          key={platform}
          className={`svd-device is-${platform}`}
          initial={{ opacity: 0, rotateY: -25, scale: 0.92 }}
          animate={{ opacity: 1, rotateY: 0, scale: 1 }}
          transition={{ type: 'spring', stiffness: 140, damping: 18 }}
        >
          {platform === 'web' ? <div className="svd-browser-bar" aria-hidden="true"><i /><i /><i /><span>app.yourbrand.com</span></div> : <span className="svd-device-notch" aria-hidden="true" />}
          <AppScreen platform={platform} run={onScreen} />
        </motion.div>
      </div>
      <p className="svd-codebase"><Code2 size={15} aria-hidden="true" /> One codebase (React Native + Next.js), shipped to all three.</p>
    </DemoFrame>
  )
}
